import { GATrackingEvent } from '../ga-tracking-event.model';
import { GATrackingEventPayload } from '../ga-tracking-event-payload.model';
import { Offer, Product } from '~app/modules/product/models';

import get from 'lodash.get';

import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';
import { Category } from '~app/modules/generic/models';
import {
  GetCategoryTreeArrayWithNameAndId,
  GetCategoryTreeNamesArray,
  GetLeafCategoryId,
} from '~app/modules/generic/helpers';
import { GaTrackingHelpersService } from '~shared/modules/ga-tracking/utils/ga-tracking-helpers.service';
import { BusinessModelEnum, BusinessTypeEnum } from '~shared/constants/business-model.enum';
import {
  GaTrackingEventName,
  GATrackingEventType,
  GaTrackingStringConstantEnum,
  GtmTemplateTypeEnum
} from '~shared/modules/ga-tracking/constants';
import { getBlackWeekPaylodProperties } from '~shared/modules/ga-tracking/utils/ga-tracking-black-week.util';
import { ProductUtil } from '~shared/utils/product.util';
import { AdditionalServicesUtil } from '~shared/utils/additional-services.util';
import { CALLBACK_ATTRIBUTE } from '~app/modules/buybox/constants/buybox.constants';

export class GATrackingProductDetailsViewEvent implements GATrackingEvent {

  pdpType: BusinessTypeEnum;

  constructor(
    private product: Product,
    private priceType: PriceTypes,
    private isBlackDealsEnabled: boolean,
    private selectedOfferId?: string,
    private breadcrumbs?: Category[],
    private isProductHasLoadbee?: boolean,
    private EECLabel?: string,
    private recommendation?: boolean
  ) {
  }

  getEventType(): GATrackingEventType {
    return GATrackingEventType.ProductDetailsView;
  }

  getGtmDataLayerTemplateType() {
    return GtmTemplateTypeEnum.ProductDetail;
  }

  getGtmDataLayerTitle() {
    return get(this.product, 'name', '');
  }

  getSelectedOffer() {
    return (this.selectedOfferId) ?
      get(this.product.offers.filter(offer => offer.id === this.selectedOfferId), '[0]', this.product.bestOffer) :
      this.product.bestOffer;
  }

  getPdpType(offer): BusinessTypeEnum {
    return get(offer, 'businessModel.value') === BusinessModelEnum.B2C
      ? BusinessTypeEnum.B2C
      : BusinessTypeEnum.B2B;
  }

  getPayload(): GATrackingEventPayload {
    const offer = this.getSelectedOffer();
    this.pdpType = this.getPdpType(offer);
    const productPrice = this.getProductPrice(offer);
    const dimension29 = GaTrackingHelpersService.getReferencePriceTypeFromOffer(offer);
    const dimension31 = GaTrackingHelpersService.getDimension31FromProduct(this.product);
    const isB2C = this.priceType === PriceTypes.b2c;
    const dimension42 = get(offer, isB2C ? 'shippingGroup.shippingCost.unitCost.gross' :
      'shippingGroup.shippingCost.unitCost.net', 0);
    const leafCategoryId = GetLeafCategoryId(this.breadcrumbs);
    const hasCallback = this.product.attributes.some(attribute => attribute.code === CALLBACK_ATTRIBUTE)
    return {
      event: GaTrackingEventName.PDPview,
      label: get(this.product, 'name', ''),
      gtmCustom: {
        priceType: this.priceType,
        'Product out of stock': this.product.offers && this.product.offers.length > 0
          ? GaTrackingStringConstantEnum.No : GaTrackingStringConstantEnum.Yes,
        IsPromoProduct: ProductUtil.hasOfferPromotion(offer)
          ? GaTrackingStringConstantEnum.Yes : GaTrackingStringConstantEnum.No,
        BrandLinkShown: this.product.brand && this.product.brand.name
          ? GaTrackingStringConstantEnum.Yes : GaTrackingStringConstantEnum.No,
        LoadbeeLinkShown: this.isProductHasLoadbee
          ? GaTrackingStringConstantEnum.Yes : GaTrackingStringConstantEnum.No,
        CallBackButtonShown: GaTrackingHelpersService.getExpressionResult(hasCallback),
        ecommerce: {
          detail: {
            actionField: {},
            products: [{
              name: get(this.product, 'name', ''),
              id: get(this.product, 'id', ''),
              price: productPrice,
              brand: get(this.product, 'brand.name', ''),
              dimension14: offer && offer.volumePrice.length > 0
              ? GaTrackingStringConstantEnum.Yes : GaTrackingStringConstantEnum.No,
              dimension42,
              category: GetCategoryTreeNamesArray(this.breadcrumbs)?.join('/'),
              category_tree: GetCategoryTreeArrayWithNameAndId(this.breadcrumbs),
              category_id: leafCategoryId,
              dimension12: get(this.product, 'hasPromotion', false),
              pdpType: this.pdpType,
              dimension27: get(offer, 'offerType', 'other'),
              dimension22: this.product.offers && this.product.offers.length > 1
              ? GaTrackingStringConstantEnum.Yes : GaTrackingStringConstantEnum.No,
              dimension15: get(offer, 'organization.shopName', ''),
              dimension32: this.EECLabel ? this.EECLabel : GaTrackingStringConstantEnum.NoEECLabel,
              dimension33: this.recommendation ? GaTrackingStringConstantEnum.Yes : GaTrackingStringConstantEnum.No,
              dimension43: GaTrackingHelpersService.getDimension43FromProduct(this.product.attributes),
              ...(!!dimension29 ? { dimension29 } : {}),
              ...(!!dimension31 ? { dimension31 } : {}),
              ...getBlackWeekPaylodProperties(this.isBlackDealsEnabled, this.product),
              dimension54: this.selectedOfferId
            }]
          }
        }
      }
    };
  }
  private getProductPrice(offer: Offer): number {
    let productPrice = 0;
    if (this.priceType === PriceTypes.b2c) {
      productPrice = get(offer, 'price.vatIncluded.amount', productPrice);
    } else {
      productPrice = get(offer, 'price.vatExcluded.amount', productPrice);
    }
    return productPrice;
  }
}
